/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */

import React, { useState, useEffect, StrictMode } from 'react';
import { Divider } from '@tvg/fuse/divider';
import { GridContainer, GridItem, GridRow } from '@tvg/fuse/grid';
import { Theme } from '@tvg/fuse/theme';
import { Typography } from '@tvg/fuse/typography';
import appWebhook from '@sd-utility/appwebhook';
import ImageCarousel from 'components/ImageCarousel/ImageCarousel';
import ProductDetails from 'components/ProductDetails/ProductDetails';
import CreditOffers from 'components/CreditOffers';
import DeliveryAndReturns from 'components/DeliveryAndReturns';
import StockMessage from 'components/StockMessage/StockMessage';
import AddToBasket from 'components/AddToBasket/AddToBasket';
import SaveForLater from 'components/SaveForLater/SaveForLater';
import ProductDescription from 'components/Description';
import RatingsAndReviews from 'components/RatingsAndReviews/RatingsAndReviews';
import NonReturnableInfo from 'components/NonReturnableInfo/NonReturnableInfo';
import PrfInfo from 'components/PrfInfo/PrfInfo';
import OptionSelectorList from 'components/OptionSelectorList/OptionSelectorList';
import SelectedSkuProvider from 'context/SelectedSku/SelectedSkuProvider';
import DynamicPrice from 'components/DynamicPrice';
import Recommendations from 'components/Recommendations/Recommendations';
import SmartOffer from 'components/SmartOffer/SmartOffer';
import QuickAdd from 'components/QuickAdd/QuickAdd';
import TrueFitWidget from 'components/TrueFitWidget';
import Personalization from 'components/Personalization/Personalization';
import LoadBee from 'components/LoadBee/LoadBee';
import InlineContent from 'components/OneWorldSync/InlineContent';
import EnergyLabel from 'components/OneWorldSync/EnergyLabel';
import TradeIn from 'components/TradeIn/TradeIn';
import { Insurance } from 'components/Insurance/Insurance';
import PromotionIdentifier from 'components/PromotionIdentifier/PromotionIdentifier';
import { DialogService } from 'components/DialogService/DialogService';
import { AddToBasketRecommendations } from 'components/AddToBasket/AddToBasketRecommendations/AddToBasketRecommendations';
import { Addon } from 'components/Addon/Addon';
import AppleRichContent from 'components/AppleRichContent/AppleRichContent';
import { ProductVariant, ProductPriceAmount } from 'types/Product';
import BrandTypes from 'constants/brand';
import WeeInfo from 'components/WeeInfo/WeeInfo';
import ShareButton from 'components/ShareButton/ShareButton';
import PDPGalleriesExtension from 'components/PDPGalleriesExtension/PDPGalleriesExtension';
import ViewMore from 'components/ViewMore/ViewMore';
import sanitizeProductName from 'utilities/product/sanitizeProductName';
import CsrfTokenError from 'utilities/logging/error-log-type/csrfTokenError';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { CI_LIB_VERSION, EventTypes } from '@very-libs/customer-interaction-event-types';
import { SiteContext } from 'context/SiteContext';
import { FeatureToggleContext } from 'context/FeatureToggle/FeatureToggleContext';
import { Customer } from 'types/CustomerTypes';
import CustomerServiceFactory, { CustomerServiceType } from 'services/customer/factory/CustomerServiceFactory';
import { ExperimentsContext } from 'context/ExperimentsContext';
import { isInsuranceRedesignEnabled, isAppleAtbRecsEnabled } from 'utilities/experiments/experimentUtilities';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { ContainerProps } from './ContainerProps';
import useEsiPortal from '../../hooks/useEsiPortal';
import { useUIStore } from '../../store';
import { shouldShowPlansAlwaysOn } from '../../utilities/plansAlwaysOn/plansAlwaysOnUtilities';
import { shouldShowSmartOfferBox } from '../../utilities/plansAlwaysOn/smartOfferSuppression';
import styles from './Container.module.css';

function ProductPageContainer(props: React.HTMLAttributes<HTMLDivElement> & Partial<ContainerProps>) {
  const { className, ...rest } = props;
  return <div {...rest} className={[styles.productPageContainer, className].filter(Boolean).join(' ')} />;
}

function StyledGridItem(props: React.ComponentProps<typeof GridItem>) {
  const { className, ...rest } = props;
  return <GridItem {...rest} className={[styles.gridItem, className].filter(Boolean).join(' ')} />;
}

function AccordionsGridItem(props: React.ComponentProps<typeof GridItem>) {
  const { className, ...rest } = props;
  return <GridItem {...rest} className={[styles.accordionsGridItem, className].filter(Boolean).join(' ')} />;
}

function ProductDetailsContainer(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  return <div {...rest} className={[styles.productDetailsContainer, className].filter(Boolean).join(' ')} />;
}

function SaveAndShareContainer(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  return <div {...rest} className={[styles.saveAndShareContainer, className].filter(Boolean).join(' ')} />;
}

function InsuranceContainer(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  return <div {...rest} className={[styles.insuranceContainer, className].filter(Boolean).join(' ')} />;
}

function DividerContainer(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  return <div {...rest} className={[styles.dividerContainer, className].filter(Boolean).join(' ')} />;
}

const logger = LoggerFactory.getLogger('Container');

export default function Container(props: ContainerProps) {
  const {
    productId,
    identification,
    images,
    brand,
    name,
    price,
    lowestPrice,
    reviews,
    skus,
    description,
    BV_URL,
    returnable,
    features,
    trueFitEnabled,
    personalizableMaxLength,
    isInsuranceAvailable,
    promotionIdentifier,
    digitalDownload,
    insuranceContent,
    roundel,
    linkedAddonServicesContent,
    tradeInValue,
    videos,
    appleRichContentKey,
    tradingDepartmentId,
    site,
    weeeEligibilityInd,
    hasDeliveryContent,
    xMockResponseValue,
    featureToggles,
    experiments = {},
    fspProduct = false,
    mockDirectives = {},
    productAttributes,
    priceMatch,
  } = props;
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [canShare, setCanShare] = useState<any | undefined>(true);
  const [platform, setPlatform] = useState('');
  const [skuList, setSkuList] = useState<ProductVariant[]>(skus);
  const [customer, setCustomer] = useState<Customer>({});
  const insuranceEsiRef = useEsiPortal('esi-insurance');
  const showPlansAlwaysOn = shouldShowPlansAlwaysOn(site, experiments, featureToggles);
  const marketingCardBffData = useUIStore((state) => state.marketingCardBffData);
  const marketingCardBffState = useUIStore((state) => state.marketingCardBffState);
  const showSmartOffer = shouldShowSmartOfferBox({
    site,
    showPlansAlwaysOn,
    marketingCardBffData,
    marketingCardBffState,
  });
  const showAppleAtbRecs =
    brand?.toLowerCase() === 'apple' && (isAppleAtbRecsEnabled(experiments) || !!featureToggles?.enableAppleAtbRecs);
  const productName = brand ? `${brand} ${name}` : name;
  useEffect(() => {
    const formattedskus = skus.map((sku) => ({
      sku: sku.option,
      sellingPrice: sku.price?.amount?.decimal ?? '',
    }));
    useUIStore.getState().setAttribute(formattedskus);
  }, [skus]);

  useEffect(() => {
    const { userAgent } = window.navigator;
    const isAppUserAgent = /SDAndroidApp|SDIPhoneApp_2\.0|SDIPhoneApp/.test(userAgent);
    if (userAgent.includes('SDIphoneApp') || userAgent.includes('SDIPhoneApp_2.0')) {
      setPlatform('iphone');
    } else if (userAgent.includes('SDAndroidApp')) {
      setPlatform('android');
    } else {
      setPlatform('web');
    }
    if (window.navigator.share === undefined && !isAppUserAgent) {
      setCanShare(false);
    }
  }, []);

  const mpn = skus[0].manufacturerPartNumber;

  const handleSetCsrfToken = async () => {
    try {
      const csrfTokenMetaTagValue = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      if (!csrfTokenMetaTagValue) {
        throw new CsrfTokenError('CSRF token is missing');
      }

      setCsrfToken(csrfTokenMetaTagValue);
    } catch (error) {
      (await logger).logWithCode(ErrorCodes.CSRF_TOKEN_ERROR, error as Error);
    }
  };
  const getWasFromPrice = (priceAmount: ProductPriceAmount): number | undefined =>
    priceAmount?.wasFromUnformatted ? priceAmount?.wasFromUnformatted : priceAmount?.unformattedPrevious;

  const handleProductView = async () => {
    const stockMessage = skus.length > 1 ? '' : skus[0].stock?.primary;
    const department = productAttributes?.find((attr) => attr.key === 'tradingDepartment')?.value[0] || '';
    const category = productAttributes?.find((attr) => attr.key === 'tradingCategory')?.value[0] || '';
    const subcategory = productAttributes?.find((attr) => attr.key === 'tradingSubCategory')?.value[0] || '';
    try {
      const ecommerce = {
        currency: price.amount.currencyCode,
        items: [
          {
            itemCategory: category,
            itemDepartment: department,
            itemLineNum: identification.itemNumber, // needs to be confirmed
            itemName: name,
            itemPrice: skus[0].price?.amount?.decimal || '""',
            itemSkuNum: skus[0].itemNumber,
            itemManufacturer: brand,
            itemRating: reviews.stars ? reviews.stars.toFixed(2) : null,
            itemRatingCnt: reviews.total ? reviews.total : null,
            itemStockMessage: stockMessage,
            itemSubCategory: subcategory,
            itemWasPrice: getWasFromPrice(price.amount)
              ? getWasFromPrice(price.amount)
              : skus[0].price?.amount?.decimal || '""',
            productGroupID: productId,
          },
        ],
      };
      window.eventBus?.publish(EventTypes.ANALYTICS_VIEW_ITEM, {
        eventType: EventTypes.ANALYTICS_VIEW_ITEM,
        eventGroup: 'Scenario', // For DataLayerFor Celebrus
        ecommerce,
        version: CI_LIB_VERSION,
      });
      // TODO DC-5620: Custom transactions (startTransaction/end) are being cancelled by Celebrus XHR interception.
      // Ticket DC-5620 will implement success event tracking using APM's viewEvent API instead.
      // See SaveForLater.tsx for similar issue and proposed solution.
      const viewEventSuccessContext = {
        productGroupID: productId,
        itemName: name,
      };
      (await logger).info('view_event success', viewEventSuccessContext);
    } catch (e) {
      const apmError = e instanceof Error ? e : new Error(String(e || 'Unknown error'));

      const captureApmError = (retryCount = 0) => {
        const apm = window.elasticApm;
        if (apm?.captureError) {
          apm?.addLabels?.({
            productGroupId: productId,
            sku: skus[0].itemNumber,
            errorCode: 'View Item Event Pulse - failed',
          });
          apm.captureError(apmError);
          return;
        }
        if (retryCount < 10) {
          setTimeout(() => {
            captureApmError(retryCount + 1);
          }, 500);
        }
      };
      captureApmError();
    }
    appWebhook('productView', {
      currencyCode: price.amount.currencyCode,
      productData: [
        {
          SKU: skus[0].option,
          catalogueID: identification.itemNumber,
          name: productName,
          quantity: 1,
          unitPrice: price.amount.decimal,
        },
      ],
    });
  };

  const stopLoadingOnApp = () => {
    appWebhook('stopLoading', true);
  };

  const customerService = CustomerServiceFactory.createCustomerService(
    featureToggles?.enableCustomerMock ? CustomerServiceType.MOCK : CustomerServiceType.ODM,
  );

  const handleGetCustomer = async () => {
    const customerResponse = await customerService.getCustomer();
    setCustomer(customerResponse);
  };

  useEffect(() => {
    stopLoadingOnApp();
    handleSetCsrfToken();
    handleGetCustomer();
    handleProductView();

    // Expose product data on window object
    const exposedData = {
      item: {
        productId,
        preDiscountPrice: lowestPrice.amount.unformattedPrice,
        itemId: identification.itemNumber, // Item Number (product level, not option number)
        retailBrandId: brand,
        productTypeCode: skus[0]?.productTypeCode,
      },
    };

    // Expose the data on window object
    (window as any).smartOfferProductData = exposedData;
    (window as any).dispatchEvent(new Event('smartOfferDataReady'));
  }, []);

  return (
    <StrictMode>
      <Theme brand={site}>
        <FeatureToggleContext.Provider value={featureToggles ?? null}>
          <SiteContext.Provider value={site}>
            <ExperimentsContext.Provider value={experiments}>
              <SelectedSkuProvider
                skus={skuList}
                setSkus={setSkuList}
                isInsuranceAvailable={isInsuranceAvailable}
                productId={productId}
                productItemNumber={identification.itemNumber}
                productName={productName}
                productPrice={price.amount}
                personalizableMaxLength={personalizableMaxLength}
                digitalDownload={digitalDownload}
                insuranceContent={insuranceContent}
                linkedAddonServicesContent={linkedAddonServicesContent}
                basketMockEnabled={!!featureToggles?.enableBasketMock}
                features={features}
                mockDirectives={mockDirectives}
                experiments={experiments}
                platform={platform}
                brand={brand}
                reviews={reviews}
                productAttributes={productAttributes}
                site={site}
              >
                <ProductPageContainer id="product-page-container" data-testid="product">
                  <GridContainer>
                    <GridRow>
                      <StyledGridItem data-testid="breadcrub-grid-item">
                        <Breadcrumb productId={productId} />
                      </StyledGridItem>
                    </GridRow>
                    <GridRow>
                      <StyledGridItem data-testid="carousel-promo" className={styles.carouselPromo} m={6} l={7}>
                        {promotionIdentifier && (
                          <PromotionIdentifier
                            text={promotionIdentifier.displayName}
                            colour={promotionIdentifier.colour}
                            productIdentifierId={promotionIdentifier.productIdentifierId}
                          />
                        )}
                        <ImageCarousel
                          images={images}
                          productTitle={productName}
                          roundel={roundel}
                          videos={videos}
                          site={site}
                        />
                      </StyledGridItem>
                      <StyledGridItem m={6} l={5}>
                        <ProductDetailsContainer>
                          <ProductDetails
                            brand={brand}
                            name={name}
                            price={price.amount}
                            reviewTotal={reviews.total}
                            reviewStars={reviews.stars}
                            productId={productId}
                            priceMatch={priceMatch}
                            showPlansAlwaysOn={showPlansAlwaysOn}
                          />
                          <EnergyLabel brand={brand} ean={skus[0].ean} mpn={mpn} option={skus[0].option} site={site} />
                          {showSmartOffer && <SmartOffer />}
                          {site !== BrandTypes.VERY && site !== BrandTypes.VERYIE && (
                            <CreditOffers
                              itemNumber={identification.itemNumber}
                              productName={productName}
                              productPriceDecimal={lowestPrice.amount.decimal}
                              site={site}
                              creditOffersMockEnabled={featureToggles?.enableCreditOffersMock}
                              skus={skus}
                            />
                          )}

                          {hasDeliveryContent && !digitalDownload && (
                            <DeliveryAndReturns
                              brand={brand}
                              site={site}
                              skus={skus}
                              csrfToken={csrfToken}
                              vdpMockEnabled={!!featureToggles?.enableVDPMock}
                            />
                          )}

                          {returnable && <NonReturnableInfo returnable={returnable} />}

                          {features?.length && skuList && skuList.length > 1 ? (
                            <>
                              <OptionSelectorList brand={brand} site={site} features={features} skus={skuList} />
                              {isInsuranceRedesignEnabled(site, experiments) && (
                                <DividerContainer>
                                  <Divider />
                                </DividerContainer>
                              )}
                            </>
                          ) : null}

                          {showAppleAtbRecs && (
                            <div className={styles.bundles}>
                              <div className={styles.bundlesHeadings}>
                                <Typography tag="h2" type="heading" size="1" data-testid="bundles-title">
                                  Always better together
                                </Typography>
                                <Typography type="paragraph" size="m2" data-testid="bundles-subheading">
                                  Based on what other customers often buy together
                                </Typography>
                              </div>
                              <AddToBasketRecommendations />
                            </div>
                          )}

                          {trueFitEnabled && (
                            <TrueFitWidget
                              locale={site === 'veryie' ? 'en_IE' : 'en_GB'}
                              itemNumber={identification.itemNumber}
                              colour={skus[0]?.options?.colour}
                              userId={customer.customerID || ''}
                            />
                          )}

                          {isInsuranceAvailable && isInsuranceRedesignEnabled(site, experiments) ? (
                            <div
                              ref={insuranceEsiRef as React.RefObject<HTMLDivElement>}
                              data-testid="insurance-redesign-container"
                              className={styles.insuranceContainer}
                            />
                          ) : (
                            isInsuranceAvailable && <Insurance />
                          )}
                          <Addon />

                          {personalizableMaxLength && <Personalization maxLength={personalizableMaxLength} />}
                          {csrfToken && (
                            <StockMessage
                              csrfToken={csrfToken}
                              isFSPEnabled={fspProduct}
                              fspMockEnabled={!!featureToggles?.enableFSPMock}
                              xMockResponseValue={xMockResponseValue}
                            />
                          )}

                          <PrfInfo prfValue={price.amount.prfValue} weeeEligibilityInd={weeeEligibilityInd} />

                          <DynamicPrice price={price.amount} />
                          <AddToBasket
                            productData={{
                              price: price.amount,
                              reviewTotal: reviews.total,
                              reviewStars: reviews.stars,
                            }}
                          />
                          <SaveAndShareContainer data-testid="save-and-share-container">
                            <SaveForLater
                              sku={skus[0]}
                              productId={productId}
                              itemNumber={identification.itemNumber}
                              mockEnabled={!!featureToggles?.enableSaveForLaterMock}
                            />
                            {canShare && (
                              <ShareButton
                                productName={sanitizeProductName(productName)}
                                site={site}
                                image={images[0]?.identifier}
                                platform={platform}
                              />
                            )}
                          </SaveAndShareContainer>
                          {tradeInValue && site !== BrandTypes.VERYIE && <TradeIn tradeInValue={tradeInValue} />}
                        </ProductDetailsContainer>
                      </StyledGridItem>
                    </GridRow>
                    <GridRow>
                      <StyledGridItem>
                        <PDPGalleriesExtension />
                      </StyledGridItem>
                    </GridRow>
                    {tradingDepartmentId && (mpn || skus[0]?.ean) ? (
                      <GridRow>
                        <AccordionsGridItem>
                          <InlineContent
                            brand={brand}
                            ean={skus[0].ean}
                            mpn={mpn}
                            option={skus[0].option}
                            Description={
                              <ProductDescription
                                itemNumber={identification.itemNumber}
                                ean={identification.ean}
                                description={description}
                                useAccordion
                              />
                            }
                            AppleContent={
                              appleRichContentKey ? <AppleRichContent contentKey={appleRichContentKey} /> : undefined
                            }
                            LoadBee={<LoadBee ean={identification.ean} />}
                            flixMediaEnabled
                          />
                        </AccordionsGridItem>
                      </GridRow>
                    ) : (
                      <GridRow>
                        <StyledGridItem>
                          <ProductDescription
                            itemNumber={identification.itemNumber}
                            ean={identification.ean}
                            description={description}
                          />
                        </StyledGridItem>

                        {appleRichContentKey && (
                          <StyledGridItem>
                            <AppleRichContent contentKey={appleRichContentKey} />
                          </StyledGridItem>
                        )}

                        <StyledGridItem>
                          <LoadBee ean={identification.ean} />
                        </StyledGridItem>

                        <StyledGridItem>
                          <InlineContent
                            brand={brand}
                            ean={skus[0].ean}
                            mpn={mpn}
                            option={skus[0].option}
                            flixMediaEnabled
                          />
                        </StyledGridItem>
                      </GridRow>
                    )}
                    <GridRow>
                      <StyledGridItem>
                        <ViewMore productId={productId} />
                      </StyledGridItem>
                    </GridRow>
                    <GridRow>
                      <StyledGridItem>
                        <Recommendations hideBorder={!!tradingDepartmentId} />
                      </StyledGridItem>
                    </GridRow>
                    <GridRow>
                      <StyledGridItem>
                        <RatingsAndReviews productId={productId} BV_URL={BV_URL} />
                      </StyledGridItem>
                    </GridRow>
                    {weeeEligibilityInd && (
                      <GridRow>
                        <StyledGridItem>
                          <WeeInfo />
                        </StyledGridItem>
                      </GridRow>
                    )}
                  </GridContainer>
                  <DialogService hasBetterTogetherRecs={showAppleAtbRecs} />
                  <QuickAdd />
                </ProductPageContainer>
              </SelectedSkuProvider>
            </ExperimentsContext.Provider>
          </SiteContext.Provider>
        </FeatureToggleContext.Provider>
      </Theme>
    </StrictMode>
  );
}
